/**
 * OnboardingOverlay — First-Time Workspace Guidance (V107)
 *
 * Non-intrusive, skippable 3-5 step walkthrough.
 * Gated by localStorage: decarboniq_onboarded_{workspace}
 * Shows only on first entry to each workspace.
 *
 * Props:
 *   workspace   — "sme" | "project" | "corporate" | "fi" | "states"
 *   onComplete  — called when dismissed or completed
 */
import React, { useState, useEffect } from "react";
import { X, ChevronRight, ChevronLeft, CheckCircle2 } from "lucide-react";

const STORAGE_KEY = (ws) => `decarboniq_onboarded_${ws}`;

const WORKSPACE_STEPS = {
  sme: {
    title:    "Welcome to your SME Workspace",
    purpose:  "Measure your emissions, verify your data, and unlock financing opportunities.",
    color:    "from-teal-900 to-[#0F172A]",
    steps: [
      {
        step:    1,
        heading: "Track Your Monthly Emissions",
        body:    "Go to Monthly Input Data and enter your electricity, fuel, and transport usage each month. We calculate your Scope 1, 2, and 3 emissions automatically.",
        action:  "Go to Monthly Input Data →",
        path:    "/sme/monthly/inputs",
        icon:    "📊",
      },
      {
        step:    2,
        heading: "Lock & Verify Your Periods",
        body:    "Once you've reviewed a month's data, lock the period to make it immutable. Then submit for verification to generate institutional-grade credentials.",
        action:  "View locked periods →",
        path:    "/sme/monthly/inputs",
        icon:    "🔒",
      },
      {
        step:    3,
        heading: "Generate Climate Credentials",
        body:    "Your credentials show auditable, traceable emissions data — suitable for reporting and financing applications. Every metric traces to its emission factor source.",
        action:  "View Credentials →",
        path:    "/sme/credentials",
        icon:    "🏅",
      },
      {
        step:    4,
        heading: "Unlock Financing Readiness",
        body:    "Your Portfolio Intelligence score determines your readiness for climate finance. Higher confidence + locked periods = stronger financing position.",
        action:  "Check Financing Readiness →",
        path:    "/sme/financing-readiness",
        icon:    "💰",
      },
    ],
  },

  project: {
    title:    "Welcome to your Project Workspace",
    purpose:  "Assess climate impact and prepare your project for investment.",
    color:    "from-blue-900 to-[#0F172A]",
    steps: [
      {
        step:    1,
        heading: "Define Your Project",
        body:    "Navigate to Input Data and fill in your project details — sector, location, technology, and baseline emissions. This forms the foundation of your impact assessment.",
        action:  "Enter Project Data →",
        path:    "/project/input",
        icon:    "📋",
      },
      {
        step:    2,
        heading: "Assess Climate Impact",
        body:    "Your Climate Snapshot shows the emissions reduction potential, intensity metrics, and carbon pathway eligibility for your project.",
        action:  "View Climate Snapshot →",
        path:    "/project/snapshot",
        icon:    "🌍",
      },
      {
        step:    3,
        heading: "Generate Credentials & Reports",
        body:    "Generate your Climate & Circularity Credentials, Finance Brief, and PDD-ready documentation for investors and regulators.",
        action:  "Go to Reports →",
        path:    "/project/reports",
        icon:    "📄",
      },
    ],
  },

  corporate: {
    title:    "Welcome to your Corporate Workspace",
    purpose:  "Track emissions, identify reduction opportunities, and structure carbon value.",
    color:    "from-indigo-900 to-[#0F172A]",
    steps: [
      {
        step:    1,
        heading: "Enter Monthly Emissions Data",
        body:    "Go to Monthly Input Data and enter your Scope 1 (fuel), Scope 2 (electricity), and Scope 3 (travel, freight, waste) activity data for each month.",
        action:  "Enter Monthly Data →",
        path:    "/corporate/monthly/inputs",
        icon:    "🏭",
      },
      {
        step:    2,
        heading: "Review IFRS S2 Intensity Metrics",
        body:    "Your IFRS S2 metrics (tCO2e/FTE, tCO2e/$M revenue) are computed automatically. These are required for institutional reporting and board disclosure.",
        action:  "View Trends & Intensity →",
        path:    "/corporate/monthly/trends",
        icon:    "📈",
      },
      {
        step:    3,
        heading: "Identify Carbon Monetisation Opportunities",
        body:    "The Carbon Monetisation scan identifies fuel switching, efficiency, and renewable opportunities ranked by potential. Top opportunities can be structured into carbon projects.",
        action:  "Run Monetisation Scan →",
        path:    "/corporate/monetisation",
        icon:    "💡",
      },
      {
        step:    4,
        heading: "Access Portfolio Intelligence",
        body:    "Your Portfolio Intelligence report shows trajectory, credibility score, risk profile, and a board-level investment narrative for use with financiers.",
        action:  "View Portfolio Intelligence →",
        path:    "/corporate/monthly/trends",
        icon:    "🎯",
      },
    ],
  },

  fi: {
    title:    "Welcome to the FI Workspace",
    purpose:  "Evaluate, structure, and finance climate and circular economy opportunities.",
    color:    "from-amber-900 to-[#0F172A]",
    steps: [
      {
        step:    1,
        heading: "Start with the Executive Dashboard",
        body:    "Your command center — pipeline value, deals requiring decision, signal scores, and capital deployment. All key metrics at a glance.",
        action:  "Go to Dashboard →",
        path:    "/fi/workspace",
        icon:    "📊",
      },
      {
        step:    2,
        heading: "Intake and Screen Deals",
        body:    "Use Deal Origination to add new deals via form or Excel upload. The Screening module shows all incoming deals with key parameters for initial triage.",
        action:  "Open Deal Intake →",
        path:    "/fi/workspace",
        icon:    "📥",
      },
      {
        step:    3,
        heading: "Run Eligibility and Credit Assessment",
        body:    "The Eligibility Engine auto-assesses each deal against the 4-gate framework. Move eligible deals to the Credit Engine for full financial analysis and committee memo.",
        action:  "Open FI Operating System →",
        path:    "/fi/workspace",
        icon:    "✅",
      },
    ],
  },

  states: {
    title:    "Welcome to the States Workspace",
    purpose:  "Aggregate climate actions and structure large-scale programmes for funding.",
    color:    "from-purple-900 to-[#0F172A]",
    steps: [
      {
        step:    1,
        heading: "Select Your State",
        body:    "Use the State Selector at the top to choose your active state. All data, programmes, and aggregation will be scoped to that state.",
        action:  "Choose State →",
        path:    "/states",
        icon:    "🗺️",
      },
      {
        step:    2,
        heading: "View Aggregated Climate Data",
        body:    "The State Climate Snapshot shows total emissions from all organizations in your state, broken down by sector. This feeds your state-level credentials.',",
        action:  "View Climate Snapshot →",
        path:    "climate-snapshot",
        icon:    "🌡️",
      },
      {
        step:    3,
        heading: "Create & Submit Programmes",
        body:    "Group eligible projects into PoA or CPA programmes. Validated programmes can be submitted to the FI pipeline for financing consideration.",
        action:  "Go to Programmes →",
        path:    "programmes",
        icon:    "📦",
      },
    ],
  },
};

export default function OnboardingOverlay({ workspace, onComplete }) {
  const [visible,  setVisible]  = useState(false);
  const [step,     setStep]     = useState(0);

  useEffect(() => {
    const done = localStorage.getItem(STORAGE_KEY(workspace));
    if (!done) {
      setVisible(true);
    }
  }, [workspace]);

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY(workspace), "true");
    setVisible(false);
    onComplete?.();
  };

  if (!visible) return null;

  const config = WORKSPACE_STEPS[workspace];
  if (!config) return null;

  const steps     = config.steps;
  const current   = steps[step];
  const isLast    = step === steps.length - 1;
  const progress  = ((step + 1) / steps.length) * 100;

  return (
    <div className="fixed inset-0 bg-black/70 z-[100] flex items-center justify-center p-4"
      data-testid="onboarding-overlay">
      <div className="bg-white rounded-3xl w-full max-w-lg shadow-2xl overflow-hidden">
        {/* Header */}
        <div className={`bg-gradient-to-br ${config.color} p-6 relative`}>
          <button onClick={dismiss}
            className="absolute top-4 right-4 text-white/50 hover:text-white/90 transition-colors"
            data-testid="onboarding-skip">
            <X size={18} />
          </button>
          <p className="text-[9px] font-bold text-white/40 uppercase tracking-widest mb-1">
            Getting Started
          </p>
          <h2 className="text-lg font-bold text-white">{config.title}</h2>
          <p className="text-[9px] text-white/60 mt-1">{config.purpose}</p>

          {/* Progress */}
          <div className="mt-4 flex gap-1">
            {steps.map((_, i) => (
              <div key={i} className={`h-1 flex-1 rounded-full transition-all ${
                i <= step ? "bg-teal-400" : "bg-white/20"
              }`} />
            ))}
          </div>
          <p className="text-[8px] text-white/40 mt-1">Step {step + 1} of {steps.length}</p>
        </div>

        {/* Step content */}
        <div className="p-6">
          <div className="text-3xl mb-3">{current.icon}</div>
          <h3 className="text-base font-bold text-[#0F172A] mb-2">{current.heading}</h3>
          <p className="text-sm text-slate-500 leading-relaxed mb-5">{current.body}</p>

          {/* Actions */}
          <div className="flex items-center justify-between">
            <button onClick={() => step > 0 && setStep(s => s - 1)}
              className={`flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-600 ${step === 0 ? "invisible" : ""}`}>
              <ChevronLeft size={14} /> Back
            </button>

            <div className="flex gap-2">
              <button onClick={dismiss}
                className="px-4 py-2 text-xs text-slate-400 hover:text-slate-600 font-medium"
                data-testid="onboarding-dismiss">
                Skip tour
              </button>
              {isLast ? (
                <button onClick={dismiss}
                  className="flex items-center gap-1.5 px-5 py-2 bg-teal-600 text-white text-xs font-bold rounded-xl hover:bg-teal-700"
                  data-testid="onboarding-complete">
                  <CheckCircle2 size={13} /> Start working
                </button>
              ) : (
                <button onClick={() => setStep(s => s + 1)}
                  className="flex items-center gap-1.5 px-5 py-2 bg-[#0F172A] text-white text-xs font-bold rounded-xl hover:bg-slate-800"
                  data-testid="onboarding-next">
                  Next <ChevronRight size={13} />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Helper to reset onboarding for a workspace (for testing)
export const resetOnboarding = (workspace) => {
  if (workspace === "all") {
    Object.keys(WORKSPACE_STEPS).forEach(ws => localStorage.removeItem(STORAGE_KEY(ws)));
  } else {
    localStorage.removeItem(STORAGE_KEY(workspace));
  }
};
